/* eslint-disable */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaExclamationTriangle } from 'react-icons/fa';
import AccessToken from '../components/accessToken/accessToken';
import { endPoints } from '../api/endpoint';
import { Box, Button, Link, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';

const colorCodes = {
    green500: '#008553',
    yellow500: '#FFC107',
};

const TableComponent = ({ driftBaseline, moduleID }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const { getAccessToken, handleTokenError } = AccessToken();
    const [selectedLabel, setSelectedLabel] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);

    // zoom state: null = closed, number = absolute index in `images` array
    const [zoomIndex, setZoomIndex] = useState(null);

    // Helper: get image URL robustly (supports strings and objects)
    const getImageUrlAt = (idx) => {
        const img = images[idx];
        if (!img) return '';
        if (typeof img === 'string') return img;
        // common field names: image_url, url, path; fallback to first value
        return img.image_url || img.url || img.path || Object.values(img)[0] || '';
    };

    // Keyboard handling for Escape and arrow navigation
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (zoomIndex !== null) {
                    setZoomIndex(null);
                } else if (showPopup) {
                    setShowPopup(false);
                }
            } else if (e.key === 'ArrowRight' && zoomIndex !== null) {
                setZoomIndex((i) => (i + 1) % images.length);
            } else if (e.key === 'ArrowLeft' && zoomIndex !== null) {
                setZoomIndex((i) => (i - 1 + images.length) % images.length);
            }
        };

        if (showPopup || zoomIndex !== null) {
            window.addEventListener('keydown', onKey);
        }
        return () => window.removeEventListener('keydown', onKey);
    }, [showPopup, zoomIndex, images.length]);

    const containerStyle = {
        padding: '20px',
        fontFamily: 'Segoe UI, Arial, sans-serif',
        color: '#333',
    };
    const btnStyle = {
        padding: '8px 16px',
        background: '#90b3ebff',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: '500',
        transition: 'background 0.2s',
    };
    const titleStyle = {
        fontSize: '20px',
        fontWeight: '600',
        marginBottom: '16px',
        color: '#0047BA',
        textAlign: 'left',
    };

    const tableStyle = {
        width: 'auto',
        minWidth: '600px',
        borderCollapse: 'collapse',
        fontSize: '14px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginLeft: '0',
    };

    const thStyle = {
        padding: '10px 12px',
        backgroundColor: '#E5F5FC',
        color: '#005CBC',
        fontWeight: '600',
        textAlign: 'left',
        whiteSpace: 'nowrap',
    };

    const thStyleCenter = {
        backgroundColor: '#E5F5FC',
        color: '#005CBC',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        paddingLeft: '30px',
        textAlign: 'left',
    };
    const tdStyle = {
        padding: '10px 12px',
        backgroundColor: '#fff',
        whiteSpace: 'nowrap',
    };

    const fetchImages = async (label) => {
        setLoading(true);
        try {
            const accessToken = await getAccessToken();
            const response = await axios.post(
                endPoints.get_image_urls,
                { model_id: moduleID?.model_id },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            const modelType = driftBaseline?.model_type;
            const imgs = response?.data?.data?.data?.images || [];

            if (modelType && typeof modelType === 'string' && modelType.toLowerCase() === 'classification') {
                // classification → flatten (keeps strings)
                setImages(imgs.map((obj) => (typeof obj === 'string' ? obj : Object.values(obj)[0])));
            } else {
                // detection → keep objects (so we can check drift_flags etc.)
                const filtered = imgs.filter((img) => {
                    if (!label) return true;
                    try {
                        return img.drift_flags?.[label] === true;
                    } catch {
                        return false;
                    }
                });
                setImages(filtered);
            }

            setSelectedLabel(label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()));
            setShowPopup(true);
            setCurrentPage(0);
        } catch (error) {
            console.error('Failed to fetch images', error);
            handleTokenError(error, endPoints.get_image_urls, 'post', { model_id: moduleID?.model_id });
        } finally {
            setLoading(false);
        }
    };
    const renderDetectionStatusBox = (score, status) => {
        const percentage = score.toFixed(2);
        if (status === 'OK') {
            return (
                <div
                    style={{
                        width: '90px',
                        borderRadius: '6px',
                        background: colorCodes.green500,
                        textAlign: 'center',
                        padding: '10px',
                        color: 'white',
                        fontWeight: '500',
                    }}
                >
                    {percentage}
                </div>
            );
        }
        return (
            <div
                style={{
                    width: '90px',
                    borderRadius: '6px',
                    background: colorCodes.yellow500,
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '500',
                }}
            >
                <FaExclamationTriangle style={{ marginRight: '6px' }} />
                {percentage}
            </div>
        );
    };

    const renderClassificationScoreBox = (score) => {
        const percentage = Math.floor(score);
        return (
            <div
                style={{
                    width: '90px',
                    borderRadius: '6px',
                    background: colorCodes.yellow500,
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '500',
                }}
            >
                <FaExclamationTriangle style={{ marginRight: '6px' }} />
                {percentage}%
            </div>
        );
    };
    const renderTableContent = () => {
        const modelType = driftBaseline?.model_type;
        const modelTypeLower = modelType && typeof modelType === 'string' ? modelType.toLowerCase() : '';

        // Classification case
        if (modelTypeLower === 'classification') {
            if (driftBaseline?.data?.total_labels > 0) {
                return (
                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={thStyle}>Label</th>
                                <th style={thStyleCenter}>Avg Score</th>
                                <th style={thStyle}>Images</th>
                            </tr>
                        </thead>
                        <tbody>
                            {driftBaseline.data.metrics.map((metric, index) => (
                                <tr key={index}>
                                    <td style={tdStyle}>{metric.label.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}</td>
                                    <td style={tdStyle}>{renderClassificationScoreBox(metric.avg_score)}</td>
                                    <td style={tdStyle}>
                                        <button
                                            type="button"
                                            onClick={() => fetchImages(metric.label)}
                                            disabled={loading}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                padding: 0,
                                                margin: 0,
                                                color: '#2d2d2eff',
                                                textDecoration: 'underline',
                                                cursor: loading ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            {loading && selectedLabel === metric.label ? 'Loading...' : 'Details'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );
            }
            return <p style={{ fontSize: '14px', color: '#555', marginTop: '8px' }}>Every label has average score above threshold value.</p>;
        }

        // Detection case
        if (modelTypeLower === 'detection' && driftBaseline?.data?.metrics_type === 'operational_drift') {
            const driftSummary = driftBaseline.data?.drift_summary || {};
            return (
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Label (Performance Matrix)</th>
                            <th style={thStyleCenter}>Drift Score</th>
                            <th style={thStyle}>Images</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(driftSummary).map(([key, value], index) => (
                            <tr key={index}>
                                <td style={tdStyle}>{key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())}</td>
                                <td style={tdStyle}>{renderDetectionStatusBox(value.score, value.status)}</td>
                                <td style={tdStyle}>
                                    <button
                                        type="button"
                                        onClick={() => fetchImages(key)}
                                        disabled={loading}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            margin: 0,
                                            color: '#2d2d2eff',
                                            textDecoration: 'underline',
                                            cursor: loading ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        {loading && selectedLabel === key ? 'Loading...' : 'Details'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }

        // Fallback: check localStorage retraining_data model_type
        try {
            const retrainingData = JSON.parse(localStorage.getItem('retraining_data'));
            const mType = retrainingData?.model_type;
            if (mType === 'Detection' || mType === 'Classification' || mType === 'classification' || mType === 'detection') {
                return <p style={{ fontSize: '14px', color: '#555', marginTop: '8px' }}>No information to display.</p>;
            }
        } catch (e) {}

        return '';
    };

    const getTitle = () => {
        try {
            const retrainingData = JSON.parse(localStorage.getItem('retraining_data'));
            const modelType = retrainingData?.model_type;
            if (modelType === 'Classification' || modelType === 'classification') return 'Drift Metric';
            if (modelType === 'Detection' || modelType === 'detection') return 'Drift Metric';
            return '';
        } catch (e) {
            console.error('Error reading retraining_data from localStorage:', e);
            return '';
        }
    };
    // when thumbnail is clicked: open zoom at the correct absolute index
    const openZoomAt = (absoluteIndex) => {
        setZoomIndex(absoluteIndex);
    };

    return (
        <div style={containerStyle}>
            <div style={titleStyle}>{getTitle()}</div>
            {renderTableContent()}

            {/* Images popup (thumbnails + pagination) */}
            {showPopup && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                    role="dialog"
                    aria-modal="true"
                    onClick={() => {
                        setShowPopup(false);
                        setZoomIndex(null);
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            padding: '20px',
                            borderRadius: '10px',
                            maxWidth: '1000px',
                            width: '90%',
                            position: 'relative',
                        }}
                        onClick={(e) => e.stopPropagation()}
                        role="document"
                    >
                        <h3 style={{ marginBottom: '10px', textAlign: 'center' }}>{selectedLabel} Images</h3>

                        {images.length > 0 ? (
                            <>
                                {/* Image Grid */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                        gap: '10px',
                                        marginBottom: '20px', // space for pagination
                                    }}
                                >
                                    {images.slice(currentPage * 12, currentPage * 12 + 12).map((img, idx) => {
                                        const absoluteIndex = currentPage * 12 + idx;
                                        const imageUrl = getImageUrlAt(absoluteIndex);
                                        const isObj = typeof images[absoluteIndex] === 'object';
                                        const isDrifted = isObj && images[absoluteIndex]?.is_drifted;

                                        return (
                                            <div key={absoluteIndex} style={{ position: 'relative' }}>
                                                <button
                                                    type="button"
                                                    onClick={() => openZoomAt(absoluteIndex)}
                                                    style={{
                                                        padding: 0,
                                                        border: '1px solid transparent',
                                                        background: 'transparent',
                                                        cursor: 'zoom-in',
                                                        width: '100%',
                                                        overflow: 'hidden',
                                                    }}
                                                    aria-label={`Open image ${absoluteIndex + 1}`}
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt={`${selectedLabel}-${absoluteIndex}`}
                                                        style={{
                                                            width: '100%',
                                                            maxHeight: '150px',
                                                            objectFit: 'cover',
                                                            borderRadius: '6px',
                                                            border: isDrifted ? '3px solid blue' : '1px solid #ccc',
                                                            display: 'block',
                                                        }}
                                                    />
                                                </button>

                                                {/* Download Button */}
                                                <a
                                                    href={imageUrl}
                                                    download
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '6px',
                                                        right: '6px',
                                                        background: 'rgba(0,0,0,0.55)',
                                                        color: '#fff',
                                                        padding: '3px 6px',
                                                        fontSize: '12px',
                                                        borderRadius: '4px',
                                                        textDecoration: 'none',
                                                    }}
                                                    aria-label="Download image"
                                                >
                                                    ⬇
                                                </a>

                                                {/* Drifted Label */}
                                                {isDrifted && (
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: '6px',
                                                            right: '6px',
                                                            background: 'rgba(123, 194, 227, 0.85)',
                                                            color: '#fff',
                                                            padding: '2px 5px',
                                                            borderRadius: '4px',
                                                            fontSize: '12px',
                                                        }}
                                                    >
                                                        Drifted
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Pagination (centered outside grid) */}
                                <Box
                                    sx={{
                                        mt: 2,
                                        display: 'flex',
                                        justifyContent: 'center', // centers horizontally
                                        alignItems: 'center',
                                        gap: 2,
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => setCurrentPage((prev) => (prev > 0 ? prev - 1 : Math.ceil(images.length / 12) - 1))}
                                    >
                                        Prev
                                    </Button>

                                    <Typography variant="body1">
                                        Page {currentPage + 1} / {Math.ceil(images.length / 12) || 1}
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        endIcon={<ArrowForwardIcon />}
                                        onClick={() => setCurrentPage((prev) => (prev < Math.ceil(images.length / 12) - 1 ? prev + 1 : 0))}
                                    >
                                        Next
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <p>No images available for {selectedLabel}.</p>
                        )}
                        <IconButton
                            onClick={() => {
                                setShowPopup(false);
                                setZoomIndex(null);
                            }}
                            size="large"
                            sx={{
                                position: 'absolute',
                                top: 15,
                                right: 15,
                                backgroundColor: 'rgba(123, 194, 227, 0.85)',
                                color: 'white',
                                '&:hover': {
                                    backgroundColor: '#003a99',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                </div>
            )}

            {/* Zoom modal (no external lib). Allows left/right navigation + download + close */}
            {zoomIndex !== null && getImageUrlAt(zoomIndex) && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.9)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                    }}
                    onClick={() => setZoomIndex(null)}
                >
                    <div onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center', maxWidth: '95vw', maxHeight: '90vh' }}>
                        <img
                            src={getImageUrlAt(zoomIndex)}
                            alt={`Preview ${zoomIndex + 1}`}
                            style={{ maxWidth: '95vw', maxHeight: '80vh', objectFit: 'contain', display: 'block', margin: '0 auto' }}
                        />

                        <Box
                            sx={{
                                mt: 1.5,
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 1.5,
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                variant="contained"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => setZoomIndex((i) => (i - 1 + images.length) % images.length)}
                            >
                                Prev
                            </Button>
                            <Link
                                href={getImageUrlAt(zoomIndex)}
                                download
                                underline="none"
                                sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <Button variant="contained" startIcon={<DownloadIcon />}>
                                    Download
                                </Button>
                            </Link>
                            <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => setZoomIndex((i) => (i + 1) % images.length)}>
                                Next
                            </Button>
                            <Button variant="contained" color="error" endIcon={<CloseIcon />} onClick={() => setZoomIndex(null)}>
                                Close
                            </Button>
                        </Box>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TableComponent;
